import { CRYPTO_ASSETS } from '../../../lib/constants'
import { config } from '../../../config'
import { formatMYR, formatCrypto, truncateAddress } from '../../../lib/utils'
import { layout, ui, cx, text, ratePill } from '../../../lib/ui-primitives'
import { BackIcon } from '../../icons'
import type { useKiosk } from '../../../hooks/useKiosk'

interface PaymentScreenProps {
  kiosk: ReturnType<typeof useKiosk>
}

export function PaymentScreen({ kiosk }: PaymentScreenProps) {
  const {
    selectedCrypto,
    selectedNetwork,
    amount,
    cryptoAmount,
    rateLockRemaining,
    userDetails,
    orderId,
    setScreen,
  } = kiosk

  if (!selectedCrypto || !selectedNetwork) return null

  const asset = CRYPTO_ASSETS[selectedCrypto]

  return (
    <div className={layout.screenContent}>
      {/* Header with back and timer */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <button
          onClick={() => setScreen('details')}
          className={ui.backBtn}
          aria-label="Go back to details"
        >
          <BackIcon aria-hidden="true" /> <span>Back</span>
        </button>
        {rateLockRemaining > 0 && (
          <div className={cx(ratePill.base, ratePill.active)}>
            <span aria-hidden="true">⏱</span>
            <span className={text.numeric}>
              {Math.floor(rateLockRemaining / 60)}:
              {(rateLockRemaining % 60).toString().padStart(2, '0')}
            </span>
          </div>
        )}
      </div>

      <div className="text-center mb-4">
        <h2 className={text.h2}>Scan to Pay</h2>
        <p className={cx('text-sm mt-1', text.secondary)}>
          Order #{orderId}
        </p>
      </div>

      <div className="flex-1 flex flex-col items-center overflow-auto">
        <div className="w-full max-w-sm">
          {/* QR Code Card */}
          <div className="bg-white rounded-3xl p-4 sm:p-5 mb-4">
            <div className="aspect-square bg-gray-100 rounded-2xl flex items-center justify-center overflow-hidden">
              {config.duitNowQrImage ? (
                <img
                  src={config.duitNowQrImage}
                  alt="DuitNow QR Code for payment"
                  className="w-full h-full object-contain"
                  width={300}
                  height={300}
                />
              ) : (
                <div className="text-center p-4">
                  <div className="text-5xl mb-3" aria-hidden="true">📱</div>
                  <p className="text-gray-600 font-medium">DuitNow QR</p>
                  <p className="text-gray-400 text-xs mt-1">Configure your QR code</p>
                </div>
              )}
            </div>
            <div className="mt-4 text-center">
              <div className={cx('text-3xl font-bold text-gray-800', text.numeric)}>
                {formatMYR(amount)}
              </div>
              <p className="text-gray-500 text-sm mt-1">
                Scan with any Malaysian banking app
              </p>
            </div>
          </div>

          {/* Order Summary */}
          <div className={cx(ui.card, 'p-4 mb-4 text-sm')}>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className={text.secondary}>Buying</span>
                <span className={cx('text-white', text.mono, text.numeric)}>
                  {formatCrypto(cryptoAmount, 4)} {selectedCrypto}
                </span>
              </div>
              <div className="flex justify-between">
                <span className={text.secondary}>Network</span>
                <span className="text-white">{selectedNetwork}</span>
              </div>
              <div className="flex justify-between">
                <span className={text.secondary}>To</span>
                <span className={cx('text-white text-xs', text.mono)}>
                  {truncateAddress(userDetails.walletAddress, 10, 8)}
                </span>
              </div>
            </div>
          </div>

          {/* Confirm Payment Button */}
          <button
            onClick={() => setScreen('confirm')}
            className={cx(ui.btnBase, ui.btnPrimary, 'w-full py-4 font-bold text-lg')}
            style={{ backgroundColor: asset.color, color: 'white' }}
          >
            I've Made Payment ✓
          </button>
        </div>
      </div>
    </div>
  )
}
